/**
 * Chunk Load Error Handler
 *
 * Automatically reloads the page when chunk loading fails during lazy loading.
 * This prevents users from seeing broken apps when deployments occur while they have an active session.
 *
 * Uses sessionStorage to prevent infinite reload loops if the failure persists
 * (e.g., CDN cache issues, offline mode, blocked assets).
 *
 * The reload guard expires after 30 seconds to allow retry if user refreshes manually.
 *
 * Usage: Call this function once at app initialization (e.g., in App.tsx)
 *
 * @example
 * ```tsx
 * import { handleChunkLoadErrors } from '@kk/shared/utils/chunk-load-error-handler';
 *
 * // At module level, before component definitions
 * handleChunkLoadErrors();
 *
 * function App() {
 *   return <Router />;
 * }
 * ```
 */

const STORAGE_KEY = 'chunk-reload-guard';
const RELOAD_DELAY = 100; // Small delay to allow logging
const GUARD_EXPIRY_MS = 30000; // 30 seconds

let hasInitialized = false;

interface ReloadGuard {
  timestamp: number;
  count: number;
}

function getReloadGuard(): ReloadGuard | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const guard = JSON.parse(stored) as ReloadGuard;
    const age = Date.now() - guard.timestamp;

    // Expire guard after 30 seconds
    if (age > GUARD_EXPIRY_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }

    return guard;
  } catch {
    return null;
  }
}

function setReloadGuard(count: number): boolean {
  try {
    const guard: ReloadGuard = {
      timestamp: Date.now(),
      count,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(guard));
    return true;
  } catch {
    // Storage unavailable (private browsing, quota exceeded)
    return false;
  }
}

function shouldReload(): boolean {
  const guard = getReloadGuard();

  // First failure - allow reload
  if (!guard) return true;

  // Already reloaded once - prevent infinite loop
  return false;
}

export function handleChunkLoadErrors() {
  // Prevent double initialization
  if (hasInitialized) return;
  hasInitialized = true;

  function isChunkLoadFailure(message: string): boolean {
    const lowerMessage = message.toLowerCase();
    return (
      lowerMessage.includes('failed to fetch dynamically imported module') ||
      lowerMessage.includes('loading chunk') ||
      lowerMessage.includes('loading css chunk') ||
      lowerMessage.includes('dynamically imported module')
    );
  }

  function attemptReload(message: string, source: string): void {
    if (shouldReload()) {
      const guard = getReloadGuard();
      const reloadCount = (guard?.count || 0) + 1;

      console.warn(
        'Chunk loading failed, reloading page to fetch latest version...',
        {
          source,
          message,
          reloadAttempt: reloadCount,
        },
      );

      // Only reload if we can persist the guard
      const guardSaved = setReloadGuard(reloadCount);
      if (!guardSaved) {
        console.error(
          'Cannot reload: sessionStorage unavailable. Chunk load error persists.',
          {
            source,
            message,
          },
        );
        return;
      }

      // Small delay to allow logging
      setTimeout(() => {
        window.location.reload();
      }, RELOAD_DELAY);
    } else {
      // Already reloaded - don't reload again to prevent infinite loop
      console.error(
        'Chunk loading failed after reload attempt. Possible causes: CDN cache, network issue, or blocked asset.',
        {
          source,
          message,
        },
      );
    }
  }

  function handleError(event: ErrorEvent) {
    const errorMessage = event.message || '';
    if (isChunkLoadFailure(errorMessage)) {
      attemptReload(errorMessage, 'error event');
    }
  }

  function handleRejection(event: PromiseRejectionEvent) {
    const reason = event.reason;
    const errorMessage =
      reason instanceof Error
        ? reason.message
        : typeof reason === 'string'
          ? reason
          : '';

    if (isChunkLoadFailure(errorMessage)) {
      attemptReload(errorMessage, 'unhandled rejection');
    }
  }

  // Use capture phase to catch errors early
  window.addEventListener('error', handleError, true);
  window.addEventListener('unhandledrejection', handleRejection);

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.info('Chunk load error handler initialized');
  }
}
